/**
 * Fetches the constantine pipeline manifest (presets + modules) from
 * the backend. Replaces the previously hardcoded preset list in
 * fixtures/constantine.manifest.ts so the editor reflects whatever
 * configs/<name>.yaml + stage/<stage>/<module>/module.yaml the
 * sidecar at this deployment's pinned SHA actually ships.
 *
 * Backend route: GET /constantine/manifest → application/json
 *   {
 *     version: string,
 *     constantine_sha: string,
 *     presets: [{ name, doc }],
 *     modules: [{ name, stage, doc }]
 *   }
 *
 * On fetch failure (network/4xx/5xx) the hook returns the embedded
 * CONSTANTINE_MANIFEST so the editor keeps working offline / in test
 * environments. The embedded copy is the source-of-truth for tests
 * (costEstimator.test.ts, benchAnchors.test.ts).
 */
import type { AxiosInstance } from 'axios';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { CONSTANTINE_MANIFEST } from '@/sections/attacks/capabilities/components/pipelineEditor/fixtures/constantine.manifest';
import type {
  ModuleManifest,
  PipelineDoc,
  PipelineManifest,
  PresetSummary,
} from '@/sections/attacks/capabilities/components/pipelineEditor/types';
import { useQuery } from '@/utils/api';

interface RawEntry {
  name: string;
  stage?: string;
  doc: Record<string, unknown>;
}

interface RawManifest {
  version: string;
  constantine_sha: string;
  presets: RawEntry[];
  modules: RawEntry[];
}

/**
 * Curated set of user-facing pipeline presets. Constantine's
 * configs/*.yaml directory ships ~35 YAMLs but the vast majority are
 * dev / smoke / bench configs for constantine's own pipeline tests —
 * not stable surfaces for Guard users to pick as starting points.
 *
 * Deliberately NOT the same set as backend constantine.go::PredefinedPipelines,
 * which drives the asset-drawer Run Tier dropdown and the OSINT submit
 * allowlist. Editor runs serialize to the custom_pipeline file parameter and
 * never hit that allowlist, so the lists are free to differ: this one still
 * offers premium-bug-rush / premium-legacy, which PredefinedPipelines dropped,
 * omits the deltascan-* tiers it added, and adds agentic-hunter-threat-model,
 * which is deliberately editor-only.
 *
 * Editing within a preset is unrestricted: picking one is a starting point,
 * not a constraint.
 */
export const CURATED_PRESET_NAMES = new Set([
  'premium-bug-rush',
  'premium-surgical',
  'premium-legacy',
  'medium',
  'basic',
  'agentic-hunter-threat-model',
]);

interface PresetDocFields {
  tier?: string;
  cost_indicator?: string;
  notes?: string;
  bench?: { notes?: string };
}

const ACRONYMS = new Set([
  'cwe',
  'cve',
  'llm',
  'sast',
  'ast',
  'api',
  'dast',
  'pov',
  'oss',
]);

/**
 * Convert a kebab-case preset/module name into a human-readable label.
 * Common acronyms (CWE, CVE, LLM, SAST, AST, API, DAST, POV, OSS) are
 * upper-cased rather than title-cased so they read naturally.
 *   "premium-legacy"           → "Premium Legacy"
 *   "cwe-fanout-prod-guard"    → "CWE Fanout Prod Guard"
 *   "scored-functions-detect"  → "Scored Functions Detect"
 */
function humanizeName(name: string): string {
  return name
    .split('-')
    .map(word => {
      if (ACRONYMS.has(word.toLowerCase())) return word.toUpperCase();
      if (!word) return word;
      return word[0].toUpperCase() + word.slice(1);
    })
    .join(' ');
}

function toPresetSummary(entry: RawEntry): PresetSummary {
  const doc = entry.doc as PipelineDoc & PresetDocFields;
  return {
    name: doc.tier ?? entry.name,
    display_name: humanizeName(entry.name),
    cost_indicator: doc.cost_indicator,
    summary: doc.bench?.notes ?? doc.notes,
    doc,
  };
}

function transformManifest(raw: RawManifest): PipelineManifest {
  const modules: Record<string, ModuleManifest> = {};
  for (const m of raw.modules) {
    modules[m.name] = m.doc as unknown as ModuleManifest;
  }
  // Filter to curated presets only. Constantine ships dev/smoke/bench
  // configs in the same directory; those reach the editor only via
  // explicit upload, never the preset picker.
  const curated = raw.presets.filter(p => CURATED_PRESET_NAMES.has(p.name));
  return {
    manifest_version: raw.version,
    modules,
    presets: curated.map(toPresetSummary),
  };
}

async function fetchManifest(axios: AxiosInstance): Promise<PipelineManifest> {
  const { data } = await axios.get<RawManifest>('/constantine/manifest');
  return transformManifest(data);
}

/**
 * Returns the constantine manifest. While the backend fetch is in
 * flight (or if it fails), the embedded fallback is returned so the
 * editor renders synchronously and never blocks on the network.
 */
export function useConstantineManifest(): {
  manifest: PipelineManifest;
  isLoading: boolean;
  isError: boolean;
} {
  const axios = useAxios();

  const query = useQuery<PipelineManifest>({
    queryKey: getQueryKey.constantineManifest(),
    queryFn: () => fetchManifest(axios),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    defaultErrorMessage: 'Failed to load constantine manifest',
  });

  return {
    manifest: query.data ?? CONSTANTINE_MANIFEST,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
